import { ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
	Button, Badge, useToast
} from '@hiring-intelligence/ui';
import { sourcingApi, SourcedCandidate, CandidateVaultDetail } from '@/services/api';
import { useCandidateVaultUpdates } from '@/services/socket';

/** Minimal shape the modal needs — full SourcedCandidate rows satisfy it, and so
 * do not-yet-saved profiles (e.g. discovery suggestions) that only carry identity. */
type CandidateDetailSubject = Pick<SourcedCandidate, 'signalhireId' | 'name'> &
	Partial<Pick<SourcedCandidate, 'currentTitle' | 'currentCompany' | 'location' | 'status'>>;

interface CandidateDetailModalProps {
	candidate: CandidateDetailSubject | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Optional action rendered in the action row (e.g. Add to Pipeline from discovery). */
	actions?: ReactNode;
	/** Optional extra content rendered above the profile sections (e.g. discovery fit rationale). */
	children?: ReactNode;
}

export function CandidateDetailModal({ candidate, open, onOpenChange, actions, children }: CandidateDetailModalProps) {
	const { toast } = useToast();
	const queryClient = useQueryClient();

	const signalhireId = candidate?.signalhireId;
	const queryKey = ['candidate-vault-detail', signalhireId];

	// Fetch vault detail when modal opens
	const { data: vaultResponse, isLoading } = useQuery({
		queryKey,
		queryFn: () => sourcingApi.getCandidateVaultDetail(signalhireId!),
		enabled: !!signalhireId && open,
	});

	// Derive vaultData directly from the query cache — single source of truth
	const vaultData: CandidateVaultDetail | null = vaultResponse?.data ?? null;

	// Listen for real-time updates via Socket.IO — write straight into the query cache
	useCandidateVaultUpdates(signalhireId, (payload) => {
		queryClient.setQueryData(queryKey, (old: any) =>
			old ? { ...old, data: payload.data } : { success: true, data: payload.data }
		);
	});

	// Request info mutation
	const requestInfoMutation = useMutation({
		mutationFn: () => sourcingApi.requestCandidateInfo(signalhireId!),
		onSuccess: (res) => {
			queryClient.setQueryData(queryKey, { success: true, data: res.data });
			toast({ title: 'Request Sent', description: 'Information request submitted. Data will arrive shortly.' });
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	// Request contacts mutation
	const requestContactsMutation = useMutation({
		mutationFn: () => sourcingApi.requestCandidateContacts(signalhireId!),
		onSuccess: (res) => {
			queryClient.setQueryData(queryKey, { success: true, data: res.data });
			toast({ title: 'Request Sent', description: 'Contact information request submitted. Data will arrive shortly.' });
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	const infoStatus = vaultData?.infoStatus;
	const contactStatus = vaultData?.contactStatus;
	const hasInfo = infoStatus === 'completed';
	const hasContacts = contactStatus === 'completed';
	const isManualCandidate = signalhireId?.startsWith('manual-');

	// SignalHire returns two different shapes for `experience`:
	//  - search results: { title, company, start_date|started, end_date|ended }
	//  - profile detail (Get Information): { position, company, started, ended, current, summary, location }
	// Render-time fallbacks so the modal works in both states.
	const getRoleTitle = (exp: any) => exp?.position || exp?.title || 'Unknown Role';
	const getRoleStart = (exp: any) => exp?.started || exp?.start_date || null;
	const getRoleEnd = (exp: any) => exp?.ended || exp?.end_date || null;
	const formatYear = (val: any) => {
		if (!val) return '?';
		const d = new Date(val);
		if (!isNaN(d.getTime())) return d.getFullYear();
		const m = String(val).match(/\d{4}/);
		return m ? m[0] : String(val);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-3">
						{vaultData?.photoUrl && (
							<img src={vaultData.photoUrl} alt="" className="w-12 h-12 rounded-full object-cover" />
						)}
						<div>
							{candidate?.name || vaultData?.fullName || 'Candidate Details'}
							{vaultData?.headline && (
								<p className="text-sm font-normal text-muted-foreground mt-0.5">{vaultData.headline}</p>
							)}
						</div>
					</DialogTitle>
					<DialogDescription>
						{candidate?.currentTitle && candidate?.currentCompany
							? `${candidate.currentTitle} at ${candidate.currentCompany}`
							: vaultData?.currentTitle && vaultData?.currentCompany
								? `${vaultData.currentTitle} at ${vaultData.currentCompany}`
								: 'View candidate details and request additional information'}
					</DialogDescription>
				</DialogHeader>

				{isLoading ? (
					<div className="flex items-center justify-center py-8">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
					</div>
				) : (
					<div className="space-y-6 pt-2">
						{/* Basic Info */}
						<div className="space-y-2">
							{(candidate?.location || vaultData?.location) && (
								<p className="text-sm text-muted-foreground">
									📍 {vaultData?.location || candidate?.location}
								</p>
							)}
							<div className="flex gap-2">
								{candidate?.status && (
									<Badge variant={candidate.status === 'sourced' ? 'secondary' : 'default'}>
										{candidate.status}
									</Badge>
								)}
								{hasInfo && <Badge variant="outline" className="border-green-500 text-green-700">Info Fetched</Badge>}
								{hasContacts && <Badge variant="outline" className="border-blue-500 text-blue-700">Contacts Fetched</Badge>}
							</div>
						</div>

						{children}

						{/* Action Buttons */}
						<div className="flex gap-3">
							{actions}
							{!hasInfo && !isManualCandidate && (
								<Button
									onClick={() => requestInfoMutation.mutate()}
									disabled={requestInfoMutation.isPending}
								>
									{(requestInfoMutation.isPending || infoStatus === 'pending') && (
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
									)}
									{requestInfoMutation.isPending ? 'Requesting...' : infoStatus === 'pending' ? 'Fetching Information... (Retry)' : 'Get Information'}
								</Button>
							)}
							{isManualCandidate && (
								<Badge variant="outline" className="border-amber-500 text-amber-700">Manually Added</Badge>
							)}
						</div>

						{/* Summary */}
						{hasInfo && vaultData?.summary && (
							<div>
								<h3 className="text-sm font-semibold mb-1">Summary</h3>
								<p className="text-sm text-muted-foreground whitespace-pre-line">{vaultData.summary}</p>
							</div>
						)}

						{/* Skills — populated from search response, no need to gate on hasInfo */}
						{vaultData?.skills && vaultData.skills.length > 0 && (
							<div>
								<h3 className="text-sm font-semibold mb-2">Skills</h3>
								<div className="flex flex-wrap gap-1.5">
									{vaultData.skills.map((skill, i) => (
										<Badge key={i} variant="secondary" className="text-xs">{skill}</Badge>
									))}
								</div>
							</div>
						)}

						{/* Experience — populated from search response, no need to gate on hasInfo */}
						{vaultData?.experience && vaultData.experience.length > 0 && (
							<div>
								<h3 className="text-sm font-semibold mb-2">Experience</h3>
								<div className="space-y-3">
									{vaultData.experience.map((exp: any, i) => {
										const start = getRoleStart(exp);
										const end = getRoleEnd(exp);
										return (
											<div key={i} className="border-l-2 border-muted pl-3">
												<div className="font-medium text-sm">{getRoleTitle(exp)}</div>
												<div className="text-sm text-muted-foreground">
													{exp.company}
													{exp.location && ` · ${exp.location}`}
												</div>
												{(start || end || exp.current) && (
													<div className="text-xs text-muted-foreground">
														{formatYear(start)} — {exp.current ? 'Present' : formatYear(end)}
													</div>
												)}
												{exp.summary && (
													<p className="text-xs text-muted-foreground mt-1">{exp.summary}</p>
												)}
											</div>
										);
									})}
								</div>
							</div>
						)}

						{/* Education */}
						{hasInfo && vaultData?.education && vaultData.education.length > 0 && (
							<div>
								<h3 className="text-sm font-semibold mb-2">Education</h3>
								<div className="space-y-2">
									{vaultData.education.map((edu, i) => (
										<div key={i} className="border-l-2 border-muted pl-3">
											<div className="font-medium text-sm">{edu.university}</div>
											<div className="text-sm text-muted-foreground">
												{edu.degree?.join(', ')} {edu.faculty && `in ${edu.faculty}`}
											</div>
											{(edu.startedYear || edu.endedYear) && (
												<div className="text-xs text-muted-foreground">
													{edu.startedYear || '?'} — {edu.endedYear || '?'}
												</div>
											)}
										</div>
									))}
								</div>
							</div>
						)}

						{/* Languages */}
						{hasInfo && vaultData?.languages && vaultData.languages.length > 0 && (
							<div>
								<h3 className="text-sm font-semibold mb-2">Languages</h3>
								<div className="flex flex-wrap gap-2">
									{vaultData.languages.map((lang, i) => (
										<Badge key={i} variant="outline" className="text-xs">
											{lang.name}{lang.proficiency && ` — ${lang.proficiency}`}
										</Badge>
									))}
								</div>
							</div>
						)}

						{/* Certifications */}
						{hasInfo && vaultData?.certifications && vaultData.certifications.length > 0 && (
							<div>
								<h3 className="text-sm font-semibold mb-2">Certifications</h3>
								<div className="space-y-1">
									{vaultData.certifications.map((cert, i) => (
										<div key={i} className="text-sm">
											<span className="font-medium">{cert.name}</span>
											{cert.authority && <span className="text-muted-foreground"> — {cert.authority}</span>}
											{cert.license && <span className="text-muted-foreground text-xs"> (#{cert.license})</span>}
										</div>
									))}
								</div>
							</div>
						)}

						{/* Organizations */}
						{hasInfo && vaultData?.organizations && vaultData.organizations.length > 0 && (
							<div>
								<h3 className="text-sm font-semibold mb-2">Organizations</h3>
								<div className="space-y-1">
									{vaultData.organizations.map((org, i) => (
										<div key={i} className="text-sm">
											<span className="font-medium">{org.name}</span>
											{org.position && <span className="text-muted-foreground"> — {org.position}</span>}
										</div>
									))}
								</div>
							</div>
						)}

						{/* Patents */}
						{hasInfo && vaultData?.patents && vaultData.patents.length > 0 && (
							<div>
								<h3 className="text-sm font-semibold mb-2">Patents</h3>
								<div className="space-y-1">
									{vaultData.patents.map((patent, i) => (
										<div key={i} className="text-sm">
											<span className="font-medium">{patent.name}</span>
											{patent.issue && <span className="text-muted-foreground"> — {patent.issue}</span>}
											{patent.date && <span className="text-muted-foreground text-xs"> ({patent.date})</span>}
										</div>
									))}
								</div>
							</div>
						)}

						{/* Publications */}
						{hasInfo && vaultData?.publications && vaultData.publications.length > 0 && (
							<div>
								<h3 className="text-sm font-semibold mb-2">Publications</h3>
								<div className="space-y-1">
									{vaultData.publications.map((pub, i) => (
										<div key={i} className="text-sm">
											<span className="font-medium">{pub.name}</span>
											{pub.issue && <span className="text-muted-foreground"> — {pub.issue}</span>}
											{pub.date && <span className="text-muted-foreground text-xs"> ({pub.date})</span>}
										</div>
									))}
								</div>
							</div>
						)}

						{/* Courses */}
						{hasInfo && vaultData?.courses && vaultData.courses.length > 0 && (
							<div>
								<h3 className="text-sm font-semibold mb-2">Courses</h3>
								<div className="flex flex-wrap gap-1.5">
									{vaultData.courses.map((course, i) => (
										<Badge key={i} variant="outline" className="text-xs">{course.name}</Badge>
									))}
								</div>
							</div>
						)}

						{/* Projects */}
						{hasInfo && vaultData?.projects && vaultData.projects.length > 0 && (
							<div>
								<h3 className="text-sm font-semibold mb-2">Projects</h3>
								<div className="space-y-1">
									{vaultData.projects.map((proj, i) => (
										<div key={i} className="text-sm">
											<span className="font-medium">{proj.name}</span>
											{proj.description && <p className="text-xs text-muted-foreground">{proj.description}</p>}
										</div>
									))}
								</div>
							</div>
						)}

						{/* Honor Awards */}
						{hasInfo && vaultData?.honorAwards && vaultData.honorAwards.length > 0 && (
							<div>
								<h3 className="text-sm font-semibold mb-2">Honors & Awards</h3>
								<div className="space-y-1">
									{vaultData.honorAwards.map((award, i) => (
										<div key={i} className="text-sm">
											<span className="font-medium">{award.name}</span>
											{award.issue && <span className="text-muted-foreground"> — {award.issue}</span>}
											{award.date && <span className="text-muted-foreground text-xs"> ({award.date})</span>}
										</div>
									))}
								</div>
							</div>
						)}

						{/* Contact Information Section — only visible after info is fetched */}
						{hasInfo && (
							<div className="border-t pt-4">
								<h3 className="text-sm font-semibold mb-2">Contact Information</h3>
								{hasContacts && vaultData?.contacts && vaultData.contacts.length > 0 ? (
									<div className="space-y-2">
										{vaultData.contacts.filter(c => c.type === 'email').length > 0 && (
											<div>
												<h4 className="text-xs font-medium text-muted-foreground mb-1">Email</h4>
												{vaultData.contacts.filter(c => c.type === 'email').map((contact, i) => (
													<div key={i} className="flex items-center gap-2 text-sm">
														<span>📧</span>
														<a href={`mailto:${contact.value}`} className="text-primary hover:underline">{contact.value}</a>
														{contact.subType && <Badge variant="outline" className="text-[10px]">{contact.subType}</Badge>}
													</div>
												))}
											</div>
										)}
										{vaultData.contacts.filter(c => c.type === 'phone').length > 0 && (
											<div>
												<h4 className="text-xs font-medium text-muted-foreground mb-1">Phone</h4>
												{vaultData.contacts.filter(c => c.type === 'phone').map((contact, i) => (
													<div key={i} className="flex items-center gap-2 text-sm">
														<span>📱</span>
														<span>{contact.value}</span>
														{contact.subType && <Badge variant="outline" className="text-[10px]">{contact.subType}</Badge>}
													</div>
												))}
											</div>
										)}
									</div>
								) : hasContacts ? (
									<p className="text-sm text-muted-foreground">Not Available</p>
								) : (
									<div className="mt-2">
										<Button
											variant="outline"
											onClick={() => requestContactsMutation.mutate()}
											disabled={requestContactsMutation.isPending}
										>
											{(requestContactsMutation.isPending || contactStatus === 'pending') && (
												<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2" />
											)}
											{requestContactsMutation.isPending ? 'Requesting...' : contactStatus === 'pending' ? 'Fetching Contacts... (Retry)' : 'Get Contact Information'}
										</Button>
									</div>
								)}
							</div>
						)}

						{/* Social Links */}
						{hasInfo && vaultData?.social && vaultData.social.length > 0 && (
							<div className="border-t pt-4">
								<h3 className="text-sm font-semibold mb-2">Social Profiles</h3>
								<div className="space-y-1">
									{vaultData.social.map((s, i) => {
										const label = s.type === 'li' ? 'LinkedIn' : s.type === 'fb' ? 'Facebook' : s.type === 'tw' ? 'Twitter' : s.type;
										return (
											<div key={i} className="flex items-center gap-2 text-sm">
												<span>{s.type === 'li' ? '🔗' : s.type === 'fb' ? '📘' : s.type === 'tw' ? '🐦' : '🌐'}</span>
												<a href={s.link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{s.link}</a>
												<Badge variant="outline" className="text-[10px]">{label}</Badge>
											</div>
										);
									})}
								</div>
							</div>
						)}
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
